import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslatableField } from '@/components/form/TranslatableField';
import { apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';

import { type LinkKind, LINKABLE_RESOURCES, type MenuItemInput, type MenuItemRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.6 — the form for one menu item.
 *
 * "Each item: label (bilingual), link type (internal record picker / named
 * route / external URL), target, icon, mega-menu description, visibility."
 *
 * The link is one choice here and three columns in §8, and the server refuses
 * an item carrying more than one of them — so the kind is a radio group and
 * changing it clears the other two rather than leaving them to be refused.
 */
interface Props {
  item: MenuItemRow | null;
  aliases: string[];
  saving: boolean;
  onSave: (input: MenuItemInput) => void;
  onCancel: () => void;
}

export function MenuItemForm({ item, aliases, saving, onSave, onCancel }: Props) {
  const { t } = useTranslation();

  const [label, setLabel] = useState<TranslatedValue>(item?.label ?? {});
  const [description, setDescription] = useState<TranslatedValue>(item?.description ?? {});
  const [kind, setKind] = useState<LinkKind>(kindOf(item));
  const [alias, setAlias] = useState<string>(item?.linkable_type ?? aliases[0] ?? 'page');
  const [recordId, setRecordId] = useState<number | null>(item?.linkable_id ?? null);
  const [routeName, setRouteName] = useState(item?.route_name ?? '');
  const [url, setUrl] = useState(item?.url ?? '');
  const [icon, setIcon] = useState(item?.icon ?? '');
  const [target, setTarget] = useState<'_self' | '_blank'>(item?.target ?? '_self');
  const [visible, setVisible] = useState(item?.is_visible ?? true);

  return (
    <form
      className="space-y-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();

        onSave({
          label,
          description,
          parent_id: item?.parent_id ?? null,
          // Exactly one of the three, because the server accepts exactly one.
          linkable_type: kind === 'record' ? alias : null,
          linkable_id: kind === 'record' ? recordId : null,
          route_name: kind === 'route' && routeName.trim() !== '' ? routeName.trim() : null,
          url: kind === 'external' && url.trim() !== '' ? url.trim() : null,
          target,
          icon: icon.trim() === '' ? null : icon.trim(),
          is_visible: visible,
          sort_order: item?.sort_order ?? 0,
        });
      }}
    >
      <TranslatableField
        label={t('menus.fields.label')}
        name="label"
        value={label}
        onChange={setLabel}
      />

      <fieldset className="space-y-4 rounded-card border border-subtle p-4">
        <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
          {t('menus.link')}
        </legend>

        {/*
          A radio group rather than a select: the three are mutually exclusive
          and which one is chosen changes the field below it, which is easier
          to follow when all three are on screen.
        */}
        <div className="flex flex-wrap gap-4">
          {(['record', 'route', 'external'] as const).map((option) => (
            <label key={option} className="flex items-center gap-2 text-body-sm text-primary">
              <input
                type="radio"
                name="link-kind"
                value={option}
                checked={kind === option}
                onChange={() => setKind(option)}
                className="h-4 w-4 border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t(`menus.kinds.${option}`)}
            </label>
          ))}
        </div>

        {kind === 'record' ? (
          <RecordPicker
            aliases={aliases}
            alias={alias}
            onAliasChange={(next) => {
              setAlias(next);
              // The id belonged to the old type. Keeping it would point the
              // item at whatever happens to share that id in the new table.
              setRecordId(null);
            }}
            recordId={recordId}
            currentLabel={item?.linkable_label ?? null}
            onPick={setRecordId}
          />
        ) : null}

        {kind === 'route' ? (
          <Input
            label={t('menus.fields.routeName')}
            hint={t('menus.fields.routeNameHint')}
            value={routeName}
            onChange={(event) => setRouteName(event.target.value)}
          />
        ) : null}

        {kind === 'external' ? (
          <Input
            label={t('menus.fields.url')}
            hint={t('menus.fields.urlHint')}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        ) : null}
      </fieldset>

      {/* §8 — "mega-menu blurbs". Only the header draws them. */}
      <TranslatableField
        label={t('menus.fields.description')}
        hint={t('menus.fields.descriptionHint')}
        name="description"
        multiline
        rows={2}
        value={description}
        onChange={setDescription}
        required={false}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('menus.fields.icon')}
          hint={t('menus.fields.iconHint')}
          value={icon}
          onChange={(event) => setIcon(event.target.value)}
        />

        <Select
          label={t('menus.fields.target')}
          value={target}
          onChange={(event) => setTarget(event.target.value as '_self' | '_blank')}
          options={[
            { value: '_self', label: t('menus.targets._self') },
            { value: '_blank', label: t('menus.targets._blank') },
          ]}
        />
      </div>

      <label className="flex items-center gap-2 text-body-sm text-primary">
        <input
          type="checkbox"
          checked={visible}
          onChange={(event) => setVisible(event.target.checked)}
          className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        />
        {t('menus.fields.visible')}
      </label>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>

        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

/**
 * §5.6's "internal record picker".
 *
 * A type, a search box and the matches. The id is what the API takes, and it
 * is deliberately not typed by hand: an editor knows the article's title, and
 * a wrong id is the failure §8's whole `linkable` design exists to prevent.
 */
function RecordPicker({
  aliases,
  alias,
  onAliasChange,
  recordId,
  currentLabel,
  onPick,
}: {
  aliases: string[];
  alias: string;
  onAliasChange: (alias: string) => void;
  recordId: number | null;
  currentLabel: TranslatedValue | null;
  onPick: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const [search, setSearch] = useState('');

  const resource = LINKABLE_RESOURCES[alias];

  const results = useQuery({
    queryKey: ['admin', 'linkable-search', resource, search],
    // Only once there is something to search for: the first page of every
    // resource is not a useful answer to "which article did you mean?".
    enabled: resource !== undefined && search.trim().length > 1,
    queryFn: () =>
      apiFetch<{ data: Array<Record<string, unknown>> }>(`/admin/${resource}`, {
        query: { search: search.trim(), per_page: 8 },
      }),
  });

  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label={t('menus.fields.recordType')}
          value={alias}
          onChange={(event) => onAliasChange(event.target.value)}
          options={aliases.map((option) => ({
            value: option,
            label: t(`menus.linkables.${option}`, option),
          }))}
        />

        <Input
          label={t('menus.fields.recordSearch')}
          hint={t('menus.fields.recordSearchHint')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {recordId !== null ? (
        <p className="text-body-sm text-secondary">
          <Chip tone="navy">{`#${recordId}`}</Chip>{' '}
          {currentLabel ? valueFor(currentLabel, locale) : t('menus.recordChosen')}
        </p>
      ) : null}

      {results.isFetching ? <Spinner size="sm" label={t('common.loading')} /> : null}

      {results.data ? (
        <ul className="divide-y divide-subtle rounded-card border border-subtle">
          {results.data.data.map((row) => {
            const id = Number(row.id);

            return (
              <li key={id}>
                <button
                  type="button"
                  className="hover:bg-surface-subtle flex w-full items-center gap-2 p-2.5 text-start text-body-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-navy-600"
                  onClick={() => onPick(id)}
                >
                  <Chip tone={id === recordId ? 'navy' : 'neutral'}>{`#${id}`}</Chip>
                  <span className="truncate text-primary">{nameOf(row, locale)}</span>
                </button>
              </li>
            );
          })}

          {results.data.data.length === 0 ? (
            <li className="p-2.5 text-body-sm text-muted">{t('menus.noRecords')}</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * What a searched row calls itself.
 *
 * Every linkable resource names itself differently — `title`, `name`,
 * `last_name` — and the picker searches ten of them, so it asks for the first
 * of those that is present rather than being told per type.
 */
function nameOf(row: Record<string, unknown>, locale: string): string {
  for (const field of ['title', 'name', 'last_name']) {
    const value = row[field];

    if (typeof value === 'object' && value !== null) {
      const translated = (value as Record<string, unknown>)[locale] ?? Object.values(value)[0];

      if (typeof translated === 'string' && translated !== '') {
        return translated;
      }
    }

    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }

  return `#${String(row.id)}`;
}

/**
 * Which of §8's three columns this item uses.
 *
 * A new item starts on `record`, which is the kind §8 prefers: it is the one
 * that cannot outlive its target, and the two others are what the current site
 * is full of.
 */
function kindOf(item: MenuItemRow | null): LinkKind {
  if (item === null || item.linkable_type !== null) {
    return 'record';
  }

  return item.route_name !== null ? 'route' : 'external';
}
